import { Injectable, OnDestroy, inject } from '@angular/core';
import { Observable, Subscription, Subject, of } from 'rxjs';
import { takeUntil, tap, catchError } from 'rxjs/operators';

import { LoggingService } from '../../logging/logging.service';
import { WebsocketService } from '../../websocket/websocket.service';
import { CacheWebSocketMessage, WebSocketMessageHandler } from '../models/cache-models';

import { CacheEventBus } from './cache-event-bus.service';

/**
 * Service for centralized management of WebSocket connections.
 * Handles subscription management and message routing.
 */
@Injectable({
  providedIn: 'root'
})
export class WebSocketManagerService<T extends {[key: string]: unknown}> implements OnDestroy {
  private readonly logger = inject(LoggingService);
  private readonly websocketService = inject(WebsocketService);
  private readonly eventBus = inject(CacheEventBus);

  // Map of topic to subscriptions
  private topicSubscriptions = new Map<string, Subscription>();

  // Registered message handlers
  private messageHandlers: WebSocketMessageHandler<T>[] = [];

  // Subject for cleanup on service destruction
  private destroy$ = new Subject<void>();

  /**
   * Register a message handler to process WebSocket messages.
   *
   * @param handler The handler to register.
   */
  public registerHandler(handler: WebSocketMessageHandler<T>): void {
    if (this.messageHandlers.includes(handler)) {
      return;
    }

    this.messageHandlers.push(handler);
  }

  /**
   * Initialize the WebSocket connection.
   *
   * @param apiBaseUrl The base URL for the API.
   */
  public initialize(apiBaseUrl: string): void {
    if (!this.websocketService.isInitialized()) {
      this.websocketService.initialize(apiBaseUrl);
      this.logger.debug('WebSocket connection initialized');
    }
  }

  /**
   * Subscribe to a WebSocket topic.
   *
   * @param topic
   * @param projectId Optional project ID for project-specific topics.
   *
   * @returns Observable that completes when subscription is established.
   */
  public subscribeToTopic(topic: string, projectId: string): Observable<void> {
    // Format the topic if projectId is provided
    const formattedTopic = `projects/${projectId}/${topic}`;

    // Check if already subscribed
    if (this.topicSubscriptions.has(formattedTopic)) {
      return of(void 0);
    }

    this.logger.debug(`Subscribing to WebSocket topic: ${formattedTopic}`);

    // Create the subscription
    const subscription = this.websocketService.watchTopic<T>(formattedTopic)
      .pipe(
        takeUntil(this.destroy$),
        tap(message => this.handleIncomingMessage({ ...message, topic: formattedTopic, projectId })),
        catchError(error => {
          this.logger.error(`Error in WebSocket subscription for topic ${formattedTopic}:`, error);
          return of(void 0);
        })
      )
      .subscribe();

    // Store the subscription for cleanup
    this.topicSubscriptions.set(formattedTopic, subscription);

    return of(void 0);
  }

  /**
   * Unsubscribe from a WebSocket topic.
   *
   * @param topic The topic to unsubscribe from.
   * @param projectId Optional project ID for project-specific topics.
   */
  public unsubscribeFromTopic(topic: string, projectId: string): void {
    const formattedTopic = `projects/${projectId}/${topic}`;

    const subscription = this.topicSubscriptions.get(formattedTopic);

    if (subscription) {
      subscription.unsubscribe();
      this.topicSubscriptions.delete(formattedTopic);
      this.logger.debug(`Unsubscribed from WebSocket topic: ${formattedTopic}`);
    }
  }

  /**
   *
   */
  public unsubscribeFromAllTopics(): void {
    for (const [topic, subscription] of this.topicSubscriptions.entries()) {
      subscription.unsubscribe();
      this.logger.debug(`Unsubscribed from WebSocket topic: ${topic}`);
    }

    this.topicSubscriptions.clear();
  }

  /**
   * Handle incoming WebSocket messages and route to appropriate handlers.
   *
   * @param message The message to handle.
   */
  private handleIncomingMessage(message: CacheWebSocketMessage<T>): void {
    this.logger.debug(`Received WebSocket message on topic: ${message.topic}`);

    // Find a handler that can process this message
    for (const handler of this.messageHandlers) {
      if (handler.canHandle(message)) {
        handler.handle(message).subscribe({
          error: (error: unknown) => this.logger.error('Error handling WebSocket message:', error)
        });

        // Also dispatch the raw message to the event bus for general cache invalidation
        this.eventBus.dispatch({
          type: 'invalidate',
          source: 'websocket',
          storeId: message.topic.split('/').pop() || message.topic,
          itemId: message.data['id'] as string,
          projectId: message.projectId,
          timestamp: Date.now()
        });

        return;
      }
    }

    // Even without a specific handler, dispatch to event bus for potential listeners
    this.eventBus.dispatch({
      type: 'invalidate',
      source: 'websocket',
      storeId: message.topic.split('/').pop() || message.topic,
      itemId: message.data['id'] as string,
      projectId: message.projectId,
      timestamp: Date.now()
    });

    this.logger.warn(`No handler found for WebSocket message on topic: ${message.topic}`);
  }

  /**
   * Clean up when the service is destroyed.
   */
  public ngOnDestroy(): void {
    this.unsubscribeFromAllTopics();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
